import { drawing } from '@progress/kendo-drawing';
import { ZOOM_START, ZOOM_END, Cursors, MOUSE_LEAVE, MOUSE_ENTER, HIT_TEST_DISTANCE } from '../../constants.js';
import { deepExtend } from '../../utils/deep-extend.js';
import { ConnectionEditTool } from './ConnectionEditTool.js';
import { ConnectionTool } from './ConnectionTool.js';
import { PointerTool } from './PointerTool.js';
import { ScrollerTool } from './ScrollerTool.js';
import { SelectionTool } from './SelectionTool.js';
import { testKey } from './testKey.js';
import { Connection } from '../../dom/Connection.js';
import { inArray } from '../../utils/functions.js';
import '../../math/Graph.js';
import { Rect } from '../../math/Shapes.js';

const round = drawing.util.round;
function hitTestShapeConnectors(shape, point) {
    let connector, position, rect;
    for (let idx = 0; idx < shape.connectors.length; idx++) {
        connector = shape.connectors[idx];
        position = connector.position();
        rect = new Rect(position.x, position.y);
        rect.inflate(HIT_TEST_DISTANCE, HIT_TEST_DISTANCE);
        if (rect.contains(point)) {
            return connector;
        }
    }
}
/**
 * The service managing the tools.
 *
 * @type {*}
 */
class ToolService {
    constructor(diagram) {
        this.diagram = diagram;
        this.tools = [
            new ScrollerTool(this),
            new ConnectionEditTool(this),
            new ConnectionTool(this),
            new SelectionTool(this),
            new PointerTool(this)
        ]; // the order matters.
        this.activeTool = undefined;
    }
    start(p, meta) {
        meta = deepExtend({}, meta);
        if (this.activeTool) {
            this.activeTool.end(p, meta);
        }
        this._updateHoveredItem(p);
        this._activateTool(p, meta);
        this.activeTool.start(p, meta);
        this._updateCursor(p);
        this.diagram.focus();
        this.diagram.canvas.surface.suspendTracking();
        this.startPoint = p;
        return true;
    }
    move(p, meta) {
        meta = deepExtend({}, meta);
        let updateHovered = true;
        if (this.activeTool) {
            updateHovered = this.activeTool.move(p, meta);
        }
        if (updateHovered) {
            this._updateHoveredItem(p);
        }
        this._updateCursor(p);
        return true;
    }
    end(p, meta) {
        meta = deepExtend({}, meta);
        if (this.activeTool) {
            this.activeTool.end(p, meta);
        }
        this.diagram.canvas.surface.resumeTracking();
        this.activeTool = undefined;
        this._updateCursor(p);
        return true;
    }
    keyDown(key, meta) {
        const diagram = this.diagram;
        meta = deepExtend({ ctrlKey: false, metaKey: false, altKey: false }, meta);
        if ((meta.ctrlKey || meta.metaKey) && !meta.altKey) { // ctrl or option
            if (testKey(key, 'a')) { // A: select all
                diagram.selectAll();
                diagram._destroyToolBar();
                return true;
            }
            else if (testKey(key, 'z')) { // Z: undo
                diagram.undo();
                diagram._destroyToolBar();
                return true;
            }
            else if (testKey(key, 'y')) { // y: redo
                diagram.redo();
                diagram._destroyToolBar();
                return true;
            }
            else if (testKey(key, 'c')) {
                diagram.copy();
                diagram._destroyToolBar();
            }
            else if (testKey(key, 'x')) {
                diagram.cut();
                diagram._destroyToolBar();
            }
            else if (testKey(key, 'v')) {
                diagram.paste();
                diagram._destroyToolBar();
            }
            else if (testKey(key, 'l')) {
                diagram.layout();
                diagram._destroyToolBar();
            }
            else if (testKey(key, 'd')) {
                diagram._destroyToolBar();
                diagram.copy();
                diagram.paste();
            }
        }
        else if (key === 46 || key === 8) { // del: deletion
            const toRemove = this.diagram._triggerRemove(diagram.select());
            if (toRemove.length) {
                this.diagram.remove(toRemove, true);
                this.diagram._syncChanges();
                this.diagram._destroyToolBar();
            }
            return true;
        }
        else if (key === 27) { // ESC: stop any action
            this._discardNewConnection();
            diagram.deselect();
            diagram._destroyToolBar();
            return true;
        }
    }
    wheel(p, meta) {
        const diagram = this.diagram;
        let z = diagram.zoom();
        const delta = meta.delta, options = diagram.options, zoomRate = options.zoomRate, zoomOptions = { point: p, meta: meta, zoom: z };
        if (diagram.trigger(ZOOM_START, zoomOptions)) {
            return;
        }
        if (delta < 0) {
            z += zoomRate;
        }
        else {
            z -= zoomRate;
        }
        z = round(Math.max(options.zoomMin, Math.min(options.zoomMax, z)), 2);
        zoomOptions.zoom = z;
        diagram.zoom(z, zoomOptions);
        diagram.trigger(ZOOM_END, zoomOptions);
        return true;
    }
    setTool(tool, index) {
        tool.toolService = this;
        this.tools[index] = tool;
    }
    selectSingle(item, meta) {
        const diagram = this.diagram;
        const selectable = diagram.options.selectable;
        if (selectable && !item.isSelected && item.options.selectable !== false) {
            const addToSelection = meta.ctrlKey && selectable.multiple !== false;
            diagram.select(item, { addToSelection: addToSelection });
        }
    }
    _discardNewConnection() {
        if (this.newConnection) {
            this.diagram.remove(this.newConnection);
            this.newConnection = undefined;
        }
    }
    _activateTool(p, meta) {
        for (let i = 0; i < this.tools.length; i++) {
            const tool = this.tools[i];
            if (tool.tryActivate(p, meta)) {
                this.activeTool = tool;
                break; // activating the first available tool in the loop.
            }
        }
    }
    _updateCursor(p) {
        const element = this.diagram.element;
        const cursor = this.activeTool ? this.activeTool.getCursor(p) : (this.hoveredAdorner ? this.hoveredAdorner._getCursor(p) : (this.hoveredItem ? this.hoveredItem._getCursor(p) : Cursors.arrow));
        element.style.cursor = cursor;
    }
    _connectionManipulation(connection, disabledShape, isNew) {
        this.activeConnection = connection;
        this.disabledShape = disabledShape;
        if (isNew) {
            this.newConnection = this.activeConnection;
        }
        else {
            this.newConnection = undefined;
        }
    }
    _updateHoveredItem(p) {
        const hit = this._hitTest(p);
        const diagram = this.diagram;
        if (hit !== this.hoveredItem && (!this.disabledShape || hit !== this.disabledShape)) {
            if (this.hoveredItem) {
                diagram.trigger(MOUSE_LEAVE, { item: this.hoveredItem });
                this.hoveredItem._hover(false);
            }
            if (hit && hit.options.enable) {
                diagram.trigger(MOUSE_ENTER, { item: hit });
                this.hoveredItem = hit; // Shape, connection or connector
                this.hoveredItem._hover(true);
            }
            else {
                this.hoveredItem = undefined;
            }
        }
    }
    _removeHover() {
        if (this.hoveredItem) {
            this.hoveredItem._hover(false);
            this.hoveredItem = undefined;
        }
    }
    _hitTest(point) {
        const d = this.diagram;
        let hit, item, i;
        // connectors
        if (this._hoveredConnector) {
            this._hoveredConnector._hover(false);
            this._hoveredConnector = undefined;
        }
        if (d._connectorsAdorner._visible) {
            hit = d._connectorsAdorner._hitTest(point);
            if (hit) {
                return hit;
            }
        }
        hit = this.diagram._resizingAdorner._hitTest(point);
        if (hit) {
            this.hoveredAdorner = d._resizingAdorner;
            if (hit.x !== 0 || hit.y !== 0) { // hit testing for resizers or rotator, otherwise if (0,0) than pass through.
                return;
            }
            hit = undefined;
        }
        else {
            this.hoveredAdorner = undefined;
        }
        if (!this.activeTool || this.activeTool.type !== 'ConnectionTool') {
            const selectedConnections = []; // only the connections should have higher presence because the connection edit point is on top of connector.
            // TODO: This should be reworked. The connection adorner should be one for all selected connections and should be hit tested prior the connections and shapes itself.
            for (i = 0; i < d._selectedItems.length; i++) {
                item = d._selectedItems[i];
                if (item instanceof Connection) {
                    selectedConnections.push(item);
                }
            }
            hit = this._hitTestItems(selectedConnections, point);
        }
        return hit || this._hitTestElements(point);
    }
    _hitTestElements(point) {
        const diagram = this.diagram;
        const shapeHit = this._hitTestItems(diagram.shapes, point);
        const connectionHit = this._hitTestItems(diagram.connections, point);
        let hit;
        if ((!this.activeTool || this.activeTool.type !== 'ConnectionTool') && shapeHit && connectionHit && !hitTestShapeConnectors(shapeHit, point)) {
            const mainLayer = diagram.mainLayer;
            const shapeIdx = inArray(shapeHit.visual, mainLayer.children);
            const connectionIdx = inArray(connectionHit.visual, mainLayer.children);
            hit = shapeIdx > connectionIdx ? shapeHit : connectionHit;
        }
        return hit || shapeHit || connectionHit;
    }
    _hitTestItems(array, point) {
        let i, item, hit;
        for (i = array.length - 1; i >= 0; i--) {
            item = array[i];
            hit = item._hitTest(point);
            if (hit) {
                return hit;
            }
        }
    }
}

export { ToolService };
